import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-400 hover:text-black transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-xl font-bold tracking-tight">Privacy Policy</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl p-6 md:p-8 prose prose-gray max-w-none">
          <p className="text-gray-500 text-sm mb-6">Last updated: September 7, 2026</p>

          <p className="text-gray-600 mb-6">
            FrameFinder ("FrameFinder", "we", "us") operates a marketplace that connects businesses with
            independent photographers and videographers ("Photographers"). This policy explains what
            information we collect, how we use it, and the choices you have.
          </p>

          <p className="text-gray-600 mb-6">
            <strong>We do not sell, rent, or trade your personal information, and we do not share it with third
            parties for their own advertising or marketing.</strong> We share data only with the service
            providers we need to run FrameFinder, and only as described below.
          </p>

          <h2 className="text-lg font-semibold mt-8 mb-4">Information We Collect</h2>
          <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
            <li><strong>Account information</strong> — your name, email address, account role (business or photographer), and password (stored only as a secure hash). If you sign in with Google, we receive your name, email address, and profile picture from Google.</li>
            <li><strong>Profile information</strong> — for photographers: portfolio images and videos, business/display name, location, hourly rate, specialties, service packages, availability, and travel schedule. For businesses: company details you choose to add.</li>
            <li><strong>Bookings and projects</strong> — project details, dates, locations, deliverables, pricing, agreements, revisions, and the messages you exchange with the other party.</li>
            <li><strong>Payment information</strong> — payments and escrow are handled by Stripe. We receive transaction status and identifiers from Stripe; we do not receive or store your full card numbers or bank details.</li>
            <li><strong>Uploaded files</strong> — portfolio media, profile and banner images, and project deliverables you upload.</li>
            <li><strong>Google Calendar data</strong> — only if you choose to connect it (see below).</li>
            <li><strong>Basic technical data</strong> — a session token to keep you logged in, and standard server logs needed to operate and secure the service.</li>
          </ul>

          <h2 className="text-lg font-semibold mt-8 mb-4">How We Use Your Information</h2>
          <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
            <li>To create and operate your account and profile.</li>
            <li>To match businesses with photographers and to show photographer availability.</li>
            <li>To process bookings, project agreements, and escrow payments.</li>
            <li>To enable messaging between a business and a photographer working together.</li>
            <li>To send transactional email — booking updates, agreement notices, password resets, and similar service messages.</li>
            <li>To keep the platform secure and to comply with legal obligations.</li>
          </ul>

          <h2 className="text-lg font-semibold mt-8 mb-4">Google Calendar Integration</h2>
          <p className="text-gray-600 mb-6">
            If a photographer connects their Google Calendar, FrameFinder requests a single, read-only
            permission: <em>free/busy</em> access
            (<code className="text-sm">https://www.googleapis.com/auth/calendar.freebusy</code>). This lets us
            see the <strong>start and end times of periods you are busy</strong> on your primary calendar. We do
            not receive, request, or store event titles, descriptions, guests, locations, or any other event
            content.
          </p>
          <p className="text-gray-600 mb-6">
            We use this free/busy information for one purpose only: to display those times as unavailable on
            your FrameFinder availability calendar so clients cannot double-book you. Busy periods are stored as
            plain "unavailable" time blocks and are refreshed periodically while your calendar is connected.
          </p>
          <p className="text-gray-600 mb-6">
            You can disconnect Google Calendar at any time from your Availability page. When you disconnect, we
            revoke our access token and delete the busy-time blocks that came from your calendar. FrameFinder's
            use and transfer of information received from Google APIs adheres to the{' '}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-black underline"
            >
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements. We do not use Google Calendar data for advertising, do not
            sell it, and do not transfer it to others except as needed to provide this feature or as required by
            law.
          </p>
          <p className="text-gray-600 mb-6">
            Separately, when one of your bookings is confirmed, FrameFinder emails you a standard calendar
            invitation (an <code className="text-sm">.ics</code> file) for that booking. Your email or calendar
            application handles that invitation; FrameFinder does not write to your calendar directly.
          </p>

          <h2 className="text-lg font-semibold mt-8 mb-4">Service Providers</h2>
          <p className="text-gray-600 mb-6">
            We rely on a small number of third-party services to operate FrameFinder. They process data only on
            our instructions and only to provide their service to us:
          </p>
          <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
            <li><strong>Stripe</strong> — payment processing and escrow.</li>
            <li><strong>Amazon Web Services (S3)</strong> — storage of uploaded images, video, and deliverables.</li>
            <li><strong>Resend</strong> — delivery of transactional email.</li>
            <li><strong>Google</strong> — optional sign-in, and the optional Calendar free/busy integration described above.</li>
            <li><strong>Our hosting and database providers</strong> — running the application and storing account data.</li>
          </ul>

          <h2 className="text-lg font-semibold mt-8 mb-4">What Other Users See</h2>
          <p className="text-gray-600 mb-6">
            A photographer's public profile — display name, location, portfolio, rates, specialties, and general
            availability — is visible to businesses browsing the platform, and may appear in search engines.
            Your email address, exact calendar details, and payment information are never shown on a public
            profile. Information exchanged inside a booking or project is visible only to the two parties
            involved.
          </p>

          <h2 className="text-lg font-semibold mt-8 mb-4">Data Retention and Deletion</h2>
          <p className="text-gray-600 mb-6">
            We keep your account information for as long as your account is active. You may request deletion of
            your account and associated personal data by contacting us at the address below; we will delete or
            anonymize it except where we are required to retain certain records (for example, transaction
            records for tax and accounting purposes).
          </p>

          <h2 className="text-lg font-semibold mt-8 mb-4">Security</h2>
          <p className="text-gray-600 mb-6">
            Passwords are stored only as salted hashes, connections are encrypted in transit, and access to
            stored data is limited to what is needed to operate the service. No method of transmission or
            storage is completely secure, but we take reasonable measures to protect your information.
          </p>

          <h2 className="text-lg font-semibold mt-8 mb-4">Children</h2>
          <p className="text-gray-600 mb-6">
            FrameFinder is not directed to anyone under 18, and we do not knowingly collect personal information
            from children.
          </p>

          <h2 className="text-lg font-semibold mt-8 mb-4">Changes to This Policy</h2>
          <p className="text-gray-600 mb-6">
            We may update this policy from time to time. When we do, we will revise the "Last updated" date
            above, and significant changes will be communicated through the platform or by email.
          </p>

          <h2 className="text-lg font-semibold mt-8 mb-4">Contact</h2>
          <p className="text-gray-600">
            Questions about this policy or your data can be sent to{' '}
            <a href="mailto:support@framefinder.io" className="text-black underline">support@framefinder.io</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
